import React from 'react';
import { BlogBlock, BlogImage } from '../../types';
import { CheckIcon, CloseIcon, QuoteIcon } from '../Icon';
import '../../styles/Blog.css';

/**
 * One block of an article. Every shape a post can contain is rendered here and
 * nowhere else, so writing a post never means writing markup: the author picks
 * a `type` in `content.ts` and gets something already styled, already
 * responsive and already correct in both themes.
 *
 * Adding a new shape is a two-step change — a variant in `BlogBlock`
 * (`types.ts`) and a `case` here. TypeScript points at this file until the new
 * variant is handled, which is the point of the discriminated union.
 */

/** Loaded lazily and sized in advance, so a picture never shifts the text. */
const Picture = ({ src, alt, caption }: BlogImage) => (
  <figure className="post-figure">
    <img className="post-figure__img" src={src} alt={alt} loading="lazy" decoding="async" />
    {caption && <figcaption className="post-figure__caption">{caption}</figcaption>}
  </figure>
);

const Bars = ({ items, title, caption }: Extract<BlogBlock, { type: 'bars' }>) => {
  // Scaled against the largest bar, never against the sum: the chart compares
  // the values to each other, which is the only thing a reader reads off it.
  const max = Math.max(...items.map((item) => Math.abs(item.value)), 1);

  return (
    <figure className="post-bars">
      {title && <figcaption className="post-bars__title">{title}</figcaption>}
      <ul className="post-bars__list">
        {items.map((item) => (
          <li key={item.label} className="post-bars__row">
            <span className="post-bars__label">{item.label}</span>
            <span className="post-bars__track">
              <span
                className={`post-bars__fill${item.highlight ? ' post-bars__fill--accent' : ''}`}
                style={{ width: `${(Math.abs(item.value) / max) * 100}%` }}
              />
            </span>
            {/* The number is printed from the same field that draws the bar,
                so the chart and its label can never disagree. */}
            <span className="post-bars__value">
              {item.value.toLocaleString('ru-RU')}
              {item.unit && <span className="post-bars__unit"> {item.unit}</span>}
            </span>
          </li>
        ))}
      </ul>
      {caption && <p className="post-bars__caption">{caption}</p>}
    </figure>
  );
};

const Block = ({ block }: { block: BlogBlock }) => {
  switch (block.type) {
    case 'text':
      return <p className="post-text">{block.text}</p>;

    case 'heading':
      return <h2 className="post-heading">{block.text}</h2>;

    case 'list':
      return block.ordered ? (
        <ol className="post-list post-list--ordered">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ol>
      ) : (
        <ul className="post-list">
          {block.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      );

    case 'image':
      return (
        <div className={block.wide ? 'post-bleed' : undefined}>
          <Picture src={block.src} alt={block.alt} caption={block.caption} />
        </div>
      );

    case 'gallery':
      return (
        <div className="post-gallery">
          {block.images.map((image) => (
            <Picture key={image.src} {...image} />
          ))}
        </div>
      );

    case 'quote':
      return (
        <figure className="post-quote">
          <QuoteIcon size={20} className="post-quote__mark" />
          <blockquote className="post-quote__text">{block.text}</blockquote>
          {block.author && (
            <figcaption className="post-quote__author">{block.author}</figcaption>
          )}
        </figure>
      );

    case 'code':
      return (
        <figure className="post-code">
          {block.lang && <span className="post-code__lang">{block.lang}</span>}
          {/* Horizontal scroll stays inside the block: a long line must never
              make the whole page scroll sideways on a phone. */}
          <pre className="post-code__pre">
            <code>{block.code}</code>
          </pre>
          {block.caption && (
            <figcaption className="post-code__caption">{block.caption}</figcaption>
          )}
        </figure>
      );

    case 'note':
      return (
        <aside className={`post-note post-note--${block.tone ?? 'info'}`}>
          {block.title && <p className="post-note__title">{block.title}</p>}
          <p className="post-note__text">{block.text}</p>
        </aside>
      );

    case 'stats':
      return (
        <figure className="post-stats">
          <div className="post-stats__row">
            {block.items.map((item) => (
              <div key={item.label} className="post-stat">
                <span className="post-stat__value">{item.value}</span>
                <span className="post-stat__label">{item.label}</span>
                {item.hint && <span className="post-stat__hint">{item.hint}</span>}
              </div>
            ))}
          </div>
          {block.caption && (
            <figcaption className="post-stats__caption">{block.caption}</figcaption>
          )}
        </figure>
      );

    case 'bars':
      return <Bars {...block} />;

    case 'compare':
      return (
        <div className="post-compare">
          {[
            { side: block.before, tone: 'before' as const, Icon: CloseIcon },
            { side: block.after, tone: 'after' as const, Icon: CheckIcon },
          ].map(({ side, tone, Icon }) => (
            <section key={tone} className={`post-compare__col post-compare__col--${tone}`}>
              <h3 className="post-compare__title">{side.title}</h3>
              <ul className="post-compare__list">
                {side.items.map((item) => (
                  <li key={item} className="post-compare__item">
                    <Icon size={14} className="post-compare__icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      );

    default:
      // Unreachable while every variant above is handled; TypeScript proves it.
      return null;
  }
};

const BlogBlocks = ({ blocks }: { blocks: BlogBlock[] }) => (
  <>
    {blocks.map((block, index) => (
      <Block key={`${block.type}-${index}`} block={block} />
    ))}
  </>
);

export default BlogBlocks;
